import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listingGrowthEngineApi,
  type AcceptanceCriterion,
  type APISpecification,
  type BusinessObjective,
  type CapabilityModule,
  type DatabaseTable,
  type EngineDashboard,
  type EngineValidation,
  type ExperimentIntegrationStep,
  type HealthScoreComponent,
  type IntelligenceEngine,
  type LGEPRDStatus,
  type ListingRecommendation,
  type PerformanceTarget,
  type TestingRequirement,
  type UXCapability,
} from '../../api/listing-growth-engine-prd';

type Tab = 'overview' | 'capabilities' | 'health' | 'intelligence' | 'recommendations' | 'experiments' | 'ux' | 'engineering' | 'status';
type Theme = 'light' | 'dark';

const priorityColors: Record<string, string> = {
  High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  Low: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
};

const riskColors: Record<string, string> = {
  Low: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function ListingGrowthEnginePRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EngineDashboard | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjective[]>([]);
  const [capabilities, setCapabilities] = useState<CapabilityModule[]>([]);
  const [health, setHealth] = useState<HealthScoreComponent[]>([]);
  const [engines, setEngines] = useState<IntelligenceEngine[]>([]);
  const [recommendations, setRecommendations] = useState<ListingRecommendation[]>([]);
  const [experimentSteps, setExperimentSteps] = useState<ExperimentIntegrationStep[]>([]);
  const [uxCaps, setUxCaps] = useState<UXCapability[]>([]);
  const [perf, setPerf] = useState<PerformanceTarget[]>([]);
  const [apis, setApis] = useState<APISpecification[]>([]);
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [testing, setTesting] = useState<TestingRequirement[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceCriterion[]>([]);
  const [status, setStatus] = useState<LGEPRDStatus | null>(null);
  const [validation, setValidation] = useState<EngineValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, obj, caps, hlth, eng, recs, steps, ux, pf, api, tbl, tst, acc, st] = await Promise.all([
        listingGrowthEngineApi.dashboard(),
        listingGrowthEngineApi.businessObjectives(),
        listingGrowthEngineApi.capabilityArchitecture(),
        listingGrowthEngineApi.healthEngine(),
        listingGrowthEngineApi.intelligenceEngines(),
        listingGrowthEngineApi.recommendations(),
        listingGrowthEngineApi.experimentIntegration(),
        listingGrowthEngineApi.uxCapabilities(),
        listingGrowthEngineApi.performanceTargets(),
        listingGrowthEngineApi.apis(),
        listingGrowthEngineApi.databaseSchema(),
        listingGrowthEngineApi.testing(),
        listingGrowthEngineApi.acceptanceCriteria(),
        listingGrowthEngineApi.status(),
      ]);
      setDashboard(dash);
      setObjectives(obj);
      setCapabilities(caps);
      setHealth(hlth);
      setEngines(eng);
      setRecommendations(recs);
      setExperimentSteps(steps);
      setUxCaps(ux);
      setPerf(pf);
      setApis(api);
      setTables(tbl);
      setTesting(tst);
      setAcceptance(acc);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Listing Growth Engine PRD');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await listingGrowthEngineApi.validate({
        attrs: {
          health_engine_ready: true, intelligence_ready: true, recommendations_ready: true,
          experiment_integration: true, performance_met: true, acceptance_passed: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Listing Growth Engine PRD passes production readiness gate' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-emerald-900/40 bg-slate-900' : 'border-emerald-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-teal-50/30 text-slate-900';
  const accent = dark ? 'text-teal-300' : 'text-emerald-700';
  const tabActive = 'bg-emerald-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'health', label: 'Health Engine' },
    { id: 'intelligence', label: 'Intelligence' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'experiments', label: 'Experiments' },
    { id: 'ux', label: 'UX' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000004</p>
          <h1 className="text-2xl font-semibold">Listing Growth Engine</h1>
          <p className={`text-sm ${muted}`}>
            AI listing intelligence, SEO optimization, content intelligence, visual intelligence, listing health, attribution and continuous optimization
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/product-workspace-asin360-prd" className={`${accent} hover:underline`}>PRD-000003</Link>
            <span className={muted}>→</span>
            <Link to="/platform/daily-growth-command-center-prd" className={`${accent} hover:underline`}>PRD-000002</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Continuous Listing Optimization</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Not about editing listings — about continuously increasing CTR, CVR, organic rank, revenue, and profit
              through evidence-backed, measurable improvements. Every change becomes an experiment.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Capability Modules', value: dashboard.capability_modules },
              { label: 'Health Components', value: dashboard.health_score_components },
              { label: 'Intelligence Engines', value: dashboard.intelligence_engines },
              { label: 'Recommendations', value: dashboard.recommendations },
              { label: 'Experiment Steps', value: dashboard.experiment_steps },
              { label: 'UX Capabilities', value: dashboard.ux_capabilities },
              { label: 'DB Tables', value: dashboard.database_tables },
              { label: 'Acceptance Criteria', value: dashboard.acceptance_criteria },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Business Objectives</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {objectives.map((o) => (
                <div key={o.key} className="rounded-lg border border-emerald-200 p-3 dark:border-emerald-800">
                  <span className="font-medium">{o.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{o.description}</p>
                  {o.kpi && <span className="mt-1 inline-block rounded bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">{o.kpi}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'capabilities' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.sort((a, b) => a.order - b.order).map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="text-xs text-emerald-600 dark:text-emerald-400">{c.order}</span>
              <h3 className="font-semibold">{c.name}</h3>
              {c.description && <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>}
              {c.integrations && c.integrations.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.integrations.map((i) => (
                    <span key={i} className="rounded bg-teal-100 px-2 py-0.5 text-xs dark:bg-teal-900">{i}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="grid gap-3 md:grid-cols-2">
          {health.map((h) => (
            <div key={h.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{h.name}</h3>
                <div className="flex gap-2">
                  <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{h.current_value}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${priorityColors[h.priority ?? ''] ?? ''}`}>{h.priority}</span>
                </div>
              </div>
              <div className="mt-2 flex gap-4 text-sm">
                <span>Target: <strong>{h.target}</strong></span>
                <span>Gap: <strong className="text-red-600 dark:text-red-400">{h.gap}</strong></span>
              </div>
              {h.business_impact && <p className={`mt-1 text-sm ${accent}`}>{h.business_impact}</p>}
              {h.ai_explanation && <p className={`mt-1 text-xs italic ${muted}`}>{h.ai_explanation}</p>}
              {h.recommended_actions && h.recommended_actions.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {h.recommended_actions.map((a) => <li key={a}>{a}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'intelligence' && (
        <div className="space-y-4">
          {engines.map((e) => (
            <div key={e.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{e.name}</h3>
                {e.section && <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">{e.section}</span>}
              </div>
              {e.description && <p className={`mt-1 text-sm ${muted}`}>{e.description}</p>}
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <h4 className={`text-xs font-semibold uppercase ${muted}`}>Analysis Dimensions</h4>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.analysis_dimensions?.map((d) => (
                      <span key={d} className="rounded border px-2 py-0.5 text-xs">{d}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className={`text-xs font-semibold uppercase ${muted}`}>Outputs</h4>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.outputs?.map((o) => (
                      <span key={o} className="rounded bg-teal-100 px-2 py-0.5 text-xs dark:bg-teal-900">{o}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{r.title}</h3>
                <div className="flex gap-2">
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium dark:bg-emerald-900">{(r.confidence * 100).toFixed(0)}%</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${riskColors[r.risk] ?? ''}`}>{r.risk} risk</span>
                </div>
              </div>
              <p className={`mt-1 text-xs ${accent}`}>{r.business_objective}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span>CTR: <strong>{r.expected_ctr_improvement}</strong></span>
                <span>CVR: <strong>{r.expected_cvr_improvement}</strong></span>
                <span>Revenue: <strong>{r.expected_revenue}</strong></span>
                <span>Profit: <strong>{r.expected_profit}</strong></span>
                <span>Effort: {r.effort} · {r.observation_window}</span>
              </div>
              {r.evidence && r.evidence.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {r.evidence.map((ev) => <li key={ev}>{ev}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="space-y-2">
          {experimentSteps.sort((a, b) => a.order - b.order).map((s) => (
            <div key={s.key} className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${card}`}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">{s.order}</span>
              <div className="flex-1">
                <span className="font-medium">{s.name}</span>
                {s.description && <p className={`text-xs ${muted}`}>{s.description}</p>}
              </div>
              {s.automated && <span className="rounded bg-teal-100 px-2 py-0.5 text-xs dark:bg-teal-900">Automated</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'ux' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {uxCaps.map((u) => (
            <div key={u.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{u.name}</h3>
              {u.mode && <code className={`text-xs ${muted}`}>{u.mode}</code>}
              {u.description && <p className={`mt-1 text-sm ${muted}`}>{u.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'engineering' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Targets</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {perf.map((p) => (
                <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{p.surface}</span>
                  <span className="ml-2 text-lg font-semibold text-emerald-600 dark:text-emerald-400">{p.max_value}{p.unit !== 'seconds' && p.unit !== 'ms' && p.unit !== 'enterprise' ? '' : p.unit === 'enterprise' ? '' : ` ${p.unit}`}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">APIs</h2>
            {apis.map((a) => (
              <div key={a.key} className={`mb-3 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold capitalize">{a.category}: {a.key}</h3>
                <p className={`text-sm ${muted}`}>{a.description}</p>
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {a.endpoints?.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Schema</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{t.name}</h3>
                  <p className={`text-sm ${muted}`}>{t.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing & Acceptance</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                {testing.map((t) => (
                  <div key={t.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <span className="font-medium capitalize">{t.level}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{t.scope}</span>
                  </div>
                ))}
              </div>
              <div>
                {acceptance.map((a) => (
                  <div key={a.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <p className="text-sm">{a.criterion}</p>
                    {a.target && <p className={`text-xs ${muted}`}>Target: {a.target}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.evidence_based && <p className={`mt-3 text-sm ${muted}`}>Evidence-based optimization — not manual editing</p>}
            {status.production_ready && <p className={`text-sm ${muted}`}>Production implementation specification complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Listing Growth Engine PRD</h3>
            <button type="button" disabled={busy} onClick={validate} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Readiness Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
